import { isResource } from '@medplum/core'
import type { Resource } from '@medplum/fhirtypes'
import {
  ExtendedResource,
  type ExtendedResourceConstructor,
} from '@seen/core/ExtendedResource'
import type { JsonLike } from '@seen/core/json'
import {
  hashKey,
  type MutateOptions,
  type QueryClient,
  type QueryKey,
  type UseMutationResult,
  useQueryClient,
} from '@tanstack/react-query'
import { useCallback } from 'react'
import { toast } from 'sonner'
import { cacheKeys } from '#src/hooks/cacheKeys.ts'
import {
  type UsePatchMutationProxy,
  type UsePatchMutationProxyOptions,
  usePatchMutation,
} from '#src/hooks/usePatchMutation.ts'

// mutateFn runs once per cached copy of the resource, and again on the latest
// version after a version conflict, so it must be pure and intent-based (see
// RefetchOnConflictOptions).
export type UseOptimisticPatchMutationProxy<T extends ExtendedResource> =
  UsePatchMutationProxy<T> & {
    // Toasted when the request fails and the cached copies are restored.
    errorMessage: string
  }

type PatchMutationResult<T extends ExtendedResource> = UseMutationResult<
  T,
  unknown,
  UsePatchMutationProxy<T>
>

type PatchMutateOptions<T extends ExtendedResource> = MutateOptions<
  T,
  unknown,
  UsePatchMutationProxy<T>
>

export type UseOptimisticPatchMutationResult<T extends ExtendedResource> = Omit<
  PatchMutationResult<T>,
  'mutate' | 'mutateAsync'
> & {
  mutate: (
    variables: UseOptimisticPatchMutationProxy<T>,
    options?: PatchMutateOptions<T>
  ) => void
  mutateAsync: (
    variables: UseOptimisticPatchMutationProxy<T>,
    options?: PatchMutateOptions<T>
  ) => Promise<T>
}

type CachedValue = JsonLike | undefined

interface OptimisticWrite {
  errorMessage: string
  previous: [QueryKey, CachedValue][]
}

interface PendingWrite {
  variables: object
  instance: ExtendedResource
  apply: (resource: Resource) => Resource
}

// Keyed by the variables object, which react-query hands unchanged to
// onMutate, mutationFn and onError.
const optimisticWrites = new WeakMap<object, OptimisticWrite>()

// Unsettled writes per `Type/id`, oldest first. A refetch or rollback that
// lands while a later write is still in flight would otherwise drop that
// write from the screen until its own save finishes.
const pendingWrites = new WeakMap<QueryClient, Map<string, PendingWrite[]>>()

function resourceRef({ fhirType, id }: ExtendedResource): string {
  return `${fhirType}/${id}`
}

function pendingFor(
  queryClient: QueryClient,
  instance: ExtendedResource
): PendingWrite[] {
  return pendingWrites.get(queryClient)?.get(resourceRef(instance)) ?? []
}

function addPending<T extends ExtendedResource>(
  queryClient: QueryClient,
  variables: UsePatchMutationProxy<T>
): void {
  const byResource =
    pendingWrites.get(queryClient) ?? new Map<string, PendingWrite[]>()
  pendingWrites.set(queryClient, byResource)
  const { instance } = variables
  const ref = resourceRef(instance)
  const write = { variables, instance, apply: optimisticEdit(variables) }
  byResource.set(ref, [...(byResource.get(ref) ?? []), write])
  watchRefetches(queryClient)
}

function removePending<T extends ExtendedResource>(
  queryClient: QueryClient,
  variables: UsePatchMutationProxy<T>
): void {
  const byResource = pendingWrites.get(queryClient)
  const ref = resourceRef(variables.instance)
  const remaining = (byResource?.get(ref) ?? []).filter(
    (pending) => pending.variables !== variables
  )
  if (remaining.length > 0) {
    byResource?.set(ref, remaining)
  } else {
    byResource?.delete(ref)
  }
}

function toPatchVariables<T extends ExtendedResource>({
  errorMessage,
  ...variables
}: UseOptimisticPatchMutationProxy<T>): UsePatchMutationProxy<T> {
  const patchVariables: UsePatchMutationProxy<T> = {
    ...variables,
    patchOptions: {
      __experimental_refetchOnConflict: true,
      ...variables.patchOptions,
    },
  }
  optimisticWrites.set(patchVariables, { errorMessage, previous: [] })
  return patchVariables
}

function holdsResource(
  value: JsonLike | undefined,
  type: string,
  id: string
): boolean {
  if (Array.isArray(value)) {
    return value.some((item: JsonLike) => holdsResource(item, type, id))
  }
  if (value instanceof ExtendedResource) {
    const resource = value.toFhirResource()
    return resource.resourceType === type && resource.id === id
  }
  return isResource(value) && value.resourceType === type && value.id === id
}

function replaceCached(
  value: JsonLike | undefined,
  type: string,
  id: string,
  apply: (resource: Resource) => Resource
): JsonLike | undefined {
  if (Array.isArray(value)) {
    return value.map((item: JsonLike) => replaceCached(item, type, id, apply))
  }
  if (!holdsResource(value, type, id)) {
    return value
  }
  if (value instanceof ExtendedResource) {
    const Ctor =
      value.constructor as ExtendedResourceConstructor<ExtendedResource>
    return Ctor.fromFhirResource(apply(value.toFhirResource()))
  }
  return isResource(value) ? apply(value) : value
}

function optimisticEdit<T extends ExtendedResource>({
  instance,
  mutateFn,
}: UsePatchMutationProxy<T>): (resource: Resource) => Resource {
  const Ctor = instance.constructor as ExtendedResourceConstructor<T>
  return (resource) => {
    const copy = Ctor.fromFhirResource(structuredClone(resource) as never)
    mutateFn(copy)
    return copy.toFhirResource()
  }
}

function applyPending(
  value: CachedValue,
  pending: PendingWrite[]
): CachedValue {
  return pending.reduce(
    (current, write) =>
      replaceCached(
        current,
        write.instance.fhirType,
        write.instance.id,
        write.apply
      ),
    value
  )
}

// usePatchMutation diffs the request against this entry, so an optimistic
// write there would leave it nothing to send. Its onSuccess fills it in.
function diffBaseHash(instance: ExtendedResource): string {
  return hashKey(
    cacheKeys.readExtended(
      instance.fhirType,
      instance.id,
      instance.constructor.name
    )
  )
}

const watchedClients = new WeakSet<QueryClient>()

// Fetched data is server state, which lacks the writes still in flight, so
// put them back on top. setQueryData marks its own updates manual, which keeps
// this from reacting to itself.
function watchRefetches(queryClient: QueryClient): void {
  if (watchedClients.has(queryClient)) {
    return
  }
  watchedClients.add(queryClient)
  queryClient.getQueryCache().subscribe((event) => {
    if (
      event.type !== 'updated' ||
      event.action.type !== 'success' ||
      event.action.manual
    ) {
      return
    }
    const { queryKey, queryHash } = event.query
    for (const pending of pendingWrites.get(queryClient)?.values() ?? []) {
      const [{ instance }] = pending
      const data = queryClient.getQueryData<JsonLike>(queryKey)
      if (
        queryHash !== diffBaseHash(instance) &&
        holdsResource(data, instance.fhirType, instance.id)
      ) {
        queryClient.setQueryData(queryKey, applyPending(data, pending))
      }
    }
  })
}

async function writeOptimistically<T extends ExtendedResource>(
  queryClient: QueryClient,
  variables: UsePatchMutationProxy<T>
): Promise<[QueryKey, CachedValue][]> {
  const { instance } = variables
  const baseHash = diffBaseHash(instance)
  const queries = queryClient
    .getQueryCache()
    .findAll({ queryKey: [instance.fhirType] })
    .filter(
      (query) =>
        query.queryHash !== baseHash &&
        holdsResource(
          queryClient.getQueryData<JsonLike>(query.queryKey),
          instance.fhirType,
          instance.id
        )
    )
  // An in-flight read would land on top of the optimistic copy.
  await Promise.all(
    queries.map((query) =>
      queryClient.cancelQueries({ queryKey: query.queryKey, exact: true })
    )
  )

  const apply = optimisticEdit(variables)
  const previous: [QueryKey, CachedValue][] = []
  for (const query of queries) {
    const data = queryClient.getQueryData<JsonLike>(query.queryKey)
    previous.push([query.queryKey, data])
    queryClient.setQueryData(
      query.queryKey,
      replaceCached(data, instance.fhirType, instance.id, apply)
    )
  }
  return previous
}

// usePatchMutation that shows the result at once: mutateFn is applied to every
// cached copy of the resource before the request goes out, the request retries
// on version conflicts, and a failed request restores those copies and toasts
// errorMessage.
export function useOptimisticPatchMutation<T extends ExtendedResource>(
  options?: UsePatchMutationProxyOptions<T> | null
): UseOptimisticPatchMutationResult<T> {
  const queryClient = useQueryClient()

  const patch = usePatchMutation<T>({
    mutation: {
      ...options?.mutation,
      onMutate: async (variables) => {
        const write = optimisticWrites.get(variables)
        if (write) {
          addPending(queryClient, variables)
          write.previous = await writeOptimistically(queryClient, variables)
        }
        return options?.mutation?.onMutate?.(variables)
      },
      onError: (error, variables, context) => {
        const write = optimisticWrites.get(variables)
        if (write) {
          removePending(queryClient, variables)
          // Later writes were applied on top of this snapshot; keep them.
          const later = pendingFor(queryClient, variables.instance)
          for (const [queryKey, data] of write.previous) {
            queryClient.setQueryData(queryKey, applyPending(data, later))
          }
          toast.error(write.errorMessage)
          cacheKeys.invalidateMutation(
            queryClient,
            variables.instance.fhirType,
            variables.instance.id
          )
        }
        options?.mutation?.onError?.(error, variables, context)
      },
      onSettled: (data, error, variables, context) => {
        removePending(queryClient, variables)
        optimisticWrites.delete(variables)
        options?.mutation?.onSettled?.(data, error, variables, context)
      },
    },
  })

  const { mutate: patchMutate, mutateAsync: patchMutateAsync } = patch
  const mutate = useCallback(
    (
      variables: UseOptimisticPatchMutationProxy<T>,
      mutateOptions?: PatchMutateOptions<T>
    ) => patchMutate(toPatchVariables(variables), mutateOptions),
    [patchMutate]
  )
  const mutateAsync = useCallback(
    (
      variables: UseOptimisticPatchMutationProxy<T>,
      mutateOptions?: PatchMutateOptions<T>
    ) => patchMutateAsync(toPatchVariables(variables), mutateOptions),
    [patchMutateAsync]
  )

  return { ...patch, mutate, mutateAsync }
}
